/**
 * Phase 6: Alerts Hook with Toast Notifications
 *
 * Connects to WebSocket for real-time alerts and displays toast notifications.
 * Also exposes methods for the AlertsCenter to refresh counts.
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import toast from 'react-hot-toast'
import type { UserAlert } from '../api'

// Alert type icons for toast
const ALERT_ICONS: Record<string, string> = {
  lineup_confirmed: '📋',
  builder_invalid: '⚠️',
  booking_warning: '🟨',
  goal: '⚽',
  card: '🟨',
  player_milestone: '🎯',
}

// Alert type colors
const ALERT_COLORS: Record<string, string> = {
  lineup_confirmed: '#4ade80', // green
  builder_invalid: '#f87171', // red
  booking_warning: '#fbbf24', // yellow
  goal: '#4ade80', // green
  card: '#fbbf24', // yellow
  player_milestone: '#60a5fa', // blue
}

export interface WebSocketAlert {
  type: 'alert'
  alert: UserAlert
}

export interface UseAlertsOptions {
  /** Whether to show toast notifications (default: true) */
  showToasts?: boolean
  /** Whether to enable WebSocket connection (default: true) */
  enabled?: boolean
  /** Optional Clerk user ID override */
  userId?: string | null
  /** Callback when new alert is received */
  onAlert?: (alert: UserAlert) => void
}

export interface UseAlertsResult {
  /** Whether WebSocket is connected */
  isConnected: boolean
  /** Connection error message */
  error: string | null
  /** Manually trigger a refresh of unread count */
  triggerRefresh: () => void
  /** Last refresh timestamp */
  lastRefreshAt: number
}

/**
 * Get WebSocket URL for alerts
 */
function getAlertsWebSocketUrl(userId?: string | null): string | null {
  if (!userId) return null
  const wsUrl = import.meta.env.VITE_WS_URL
  if (wsUrl) {
    return `${wsUrl}/v1/ws/alerts/${userId}`
  }

  const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8005/v1'
  const wsBase = apiBase
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:')
    .replace(/\/v1$/, '')

  return `${wsBase}/v1/ws/alerts/${userId}`
}

/**
 * Show a toast notification for an alert
 */
function showAlertToast(alert: UserAlert) {
  const icon = ALERT_ICONS[alert.alert_type] || '🔔'
  const color = ALERT_COLORS[alert.alert_type] || '#60a5fa'

  toast(
    (t) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: '300px',
        }}
      >
        <span style={{ fontSize: '20px', flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#f5f5f5',
              marginBottom: '4px',
            }}
          >
            {alert.title}
          </div>
          {alert.message && (
            <div
              style={{
                fontSize: '13px',
                color: '#a1a1aa',
                lineHeight: '1.4',
              }}
            >
              {alert.message}
            </div>
          )}
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          style={{
            background: 'none',
            border: 'none',
            color: '#71717a',
            cursor: 'pointer',
            padding: '0',
            fontSize: '18px',
            lineHeight: '1',
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
    ),
    {
      duration: 5000,
      style: {
        background: '#18181b',
        border: `1px solid ${color}40`,
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
      },
    }
  )
}

/**
 * Hook to subscribe to real-time alerts via WebSocket and show toast notifications.
 *
 * @example
 * const { isConnected } = useAlerts({
 *   onAlert: (alert) => {
 *     // Update unread count
 *     setUnreadCount(prev => prev + 1)
 *   }
 * })
 */
export function useAlerts(options: UseAlertsOptions = {}): UseAlertsResult {
  const { showToasts = true, enabled = true, onAlert, userId } = options
  const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false, userId: null }
  const resolvedUserId = userId ?? auth.userId

  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshAt, setLastRefreshAt] = useState(Date.now())

  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<number | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const onAlertRef = useRef(onAlert)

  // Keep callback ref current
  useEffect(() => {
    onAlertRef.current = onAlert
  }, [onAlert])

  const clearTimers = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!enabled || !resolvedUserId) return

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close()
    }
    clearTimers()

    const url = getAlertsWebSocketUrl(resolvedUserId)
    if (!url) {
      return
    }

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        setError(null)
        reconnectAttemptsRef.current = 0

        // Heartbeat every 30s
        heartbeatRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          // Ignore pong/ack messages
          if (data.type === 'pong' || data.type === 'connected') {
            return
          }

          // Handle alert message
          if (data.type === 'alert' && data.alert) {
            const alert = data.alert as UserAlert

            // Show toast notification
            if (showToasts) {
              showAlertToast(alert)
            }

            // Call callback
            if (onAlertRef.current) {
              onAlertRef.current(alert)
            }

            // Trigger refresh for AlertsCenter
            setLastRefreshAt(Date.now())
          }
        } catch (e) {
          console.warn('Failed to parse alerts WebSocket message:', e)
        }
      }

      ws.onerror = () => {
        setError('Connection error')
      }

      ws.onclose = (event) => {
        setIsConnected(false)
        clearTimers()

        // Don't reconnect if intentionally closed
        if (!enabled || event.code === 1000) {
          return
        }

        // Exponential backoff reconnection
        reconnectAttemptsRef.current += 1
        if (reconnectAttemptsRef.current <= 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000)
          reconnectTimeoutRef.current = window.setTimeout(() => {
            connect()
          }, delay)
          setError(`Reconnecting...`)
        } else {
          setError('Alerts disconnected')
        }
      }
    } catch (e) {
      setError('Failed to connect')
      console.error('Alerts WebSocket connection failed:', e)
    }
  }, [enabled, showToasts, clearTimers, resolvedUserId])

  const triggerRefresh = useCallback(() => {
    setLastRefreshAt(Date.now())
  }, [])

  // Connect on mount
  useEffect(() => {
    if (enabled && resolvedUserId) {
      connect()
    }

    return () => {
      clearTimers()
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted')
        wsRef.current = null
      }
    }
  }, [enabled, connect, clearTimers, resolvedUserId])

  return {
    isConnected,
    error,
    triggerRefresh,
    lastRefreshAt,
  }
}
